import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InfoIcon } from "./icons";
import { OtpCodeInput } from "./OtpCodeInput";
import { MAX_OTP_ATTEMPTS, OTP_RESEND_COOLDOWN_SECONDS } from "./handler/otp.helper";
import { OtpOutcome } from "./handler/useOtpSend";

export interface VerifyStepProps {
  phoneNumber: string;
  flightNumber: string;
  onVerify: (code: string) => Promise<OtpOutcome>;
  onResend: () => Promise<OtpOutcome>;
  isVerifying: boolean;
  onBack: () => void;
}

type VerifyMessage =
  | { type: "wrong"; attemptsRemaining: number }
  | { type: "exhausted" }
  | { type: "otherError" }
  | null;

// Server-enforced attempt cap (AuthV2Service.OTP_DEFAULT_TRY_COUNT) — the verify response never
// returns a remaining-attempts count, so this mirrors the same limit locally purely for copy.
const OTP_INVALID_CODE = "OT_004";
const OTP_MAX_ATTEMPTS_EXCEEDED = "OT_003";

export const VerifyStep: React.FC<VerifyStepProps> = ({
  phoneNumber,
  flightNumber,
  onVerify,
  onResend,
  isVerifying,
  onBack,
}) => {
  const { t } = useTranslation("flightAlerts");
  const [code, setCode] = useState("");
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [message, setMessage] = useState<VerifyMessage>(null);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(
    OTP_RESEND_COOLDOWN_SECONDS,
  );

  const [resendKey, setResendKey] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setResendSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendKey]);

  const isExhausted = message?.type === "exhausted";
  const canResend = resendSecondsLeft <= 0;

  const handleVerify = async () => {
    if (isExhausted || isVerifying) return;
    const result = await onVerify(code);
    if (result.success) return; // container advances the wizard step
    setCode("");
    if (result.errorCode === OTP_MAX_ATTEMPTS_EXCEEDED) {
      setMessage({ type: "exhausted" });
      return;
    }
    if (result.errorCode === OTP_INVALID_CODE) {
      setAttemptsUsed((prev) => {
        const next = prev + 1;
        setMessage({
          type: "wrong",
          attemptsRemaining: Math.max(MAX_OTP_ATTEMPTS - next, 0),
        });
        return next;
      });
      return;
    }
    setMessage({ type: "otherError" });
  };

  const handleResend = async () => {
    if (!canResend) return;
    setCode("");
    setMessage(null);
    const result = await onResend();
    if (result.success) {
      setAttemptsUsed(0);
      setResendSecondsLeft(OTP_RESEND_COOLDOWN_SECONDS);
      setResendKey((prev) => prev + 1);
    } else {
      setMessage({ type: "otherError" });
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="self-start font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60"
      >
        {t("optin.backLink", { flightNumber })}
      </button>

      <div className="flex flex-col gap-4 md:gap-6 md:rounded-2xl md:border-2 md:border-ink-12 md:bg-card md:p-8">
        <h1 className="font-lexend text-[28px]/[32px] font-bold tracking-[-1px] text-ink-100 md:text-[24px]/[28px] md:tracking-[-0.8px]">
          {t("verify.heading")}
        </h1>

        <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60 md:text-[16px]/[20px] md:tracking-[-0.1px]">
          {t("verify.subtitle", { phone: phoneNumber })}
        </p>

        <OtpCodeInput value={code} onChange={setCode} />

        {message && (
          <div className="flex items-start gap-2 rounded-xl bg-danger-tint p-3">
            <InfoIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-danger-100" />
            <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
              {message.type === "wrong"
                ? t("verify.errWrongCode", { count: message.attemptsRemaining })
                : message.type === "exhausted"
                  ? t("verify.errExhausted")
                  : t("verify.errGeneric")}
            </p>
          </div>
        )}

        <button
          type="button"
          disabled={isExhausted || isVerifying}
          onClick={handleVerify}
          className={`flex h-14 w-full items-center justify-center rounded-2xl font-lexend text-[16px]/[20px] font-bold tracking-normal text-white ${
            isExhausted || isVerifying ? "cursor-not-allowed bg-gray-300" : "bg-primary-100"
          }`}
        >
          {isVerifying ? t("verify.verifying") : t("verify.submit")}
        </button>

        <button
          type="button"
          disabled={!canResend || isVerifying}
          onClick={handleResend}
          className={`mx-auto text-center font-lexend text-[12px]/[16px] font-normal tracking-normal md:text-[14px]/[18px] ${
            canResend ? "text-ink-60" : "text-ink-40 cursor-not-allowed"
          }`}
        >
          {canResend
            ? t("verify.resend")
            : t("verify.resendCountdown", { seconds: resendSecondsLeft })}
        </button>
      </div>
    </>
  );
};
